import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/useAuthStore';
import { useI18n } from '../contexts/I18nContext';
import { AuthShell, AuthError, AuthSuccess } from '../components/auth/AuthShell';
import * as authApi from '../lib/authApi';

export function RegisterPage() {
  const navigate = useNavigate();
  const registerAction = useAuthStore((s) => s.register);
  const { t } = useI18n();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Self-signup with SMTP configured: account created but unverified — the
  // user must click the emailed link before logging in.
  const [checkEmail, setCheckEmail] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError(t('register.passwordMin'));
      return;
    }

    setSubmitting(true);
    try {
      const { verificationRequired } = await registerAction(email, password, name);
      if (verificationRequired) {
        setCheckEmail(true);
        return;
      }
      // Every user lands on the daily summary after login (CLE-448).
      navigate(authApi.postAuthLandingPath(), { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('register.failed'));
    } finally {
      setSubmitting(false);
    }
  }

  if (checkEmail) {
    return (
      <AuthShell title={t('register.checkEmail.title')}>
        <AuthSuccess message={t('register.checkEmail.body', { email })} />
        <p className="text-center text-sm">
          <Link to="/login" style={{ color: '#A78BFA' }} className="underline">
            {t('register.checkEmail.backToLogin')}
          </Link>
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell title={t('register.title')}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <AuthError message={error} />}

        <label className="block text-sm">
          <span style={{ color: '#94A3B8' }}>{t('register.name')}</span>
          <input
            type="text"
            required
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="login-field mt-1 w-full px-3 py-2 outline-none"
          />
        </label>

        <label className="block text-sm">
          <span style={{ color: '#94A3B8' }}>{t('register.email')}</span>
          <input
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="login-field mt-1 w-full px-3 py-2 outline-none"
          />
        </label>

        <label className="block text-sm">
          <span style={{ color: '#94A3B8' }}>{t('register.password')}</span>
          <input
            type="password"
            required
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="login-field mt-1 w-full px-3 py-2 outline-none"
          />
        </label>

        <button
          type="submit"
          disabled={submitting}
          className="login-submit w-full px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
        >
          {submitting ? t('register.submitting') : t('register.submit')}
        </button>

        <p className="text-center text-sm" style={{ color: '#64748B' }}>
          {t('register.hasAccount')}{' '}
          <Link to="/login" style={{ color: '#A78BFA' }} className="underline">
            {t('register.login')}
          </Link>
        </p>
        <p className="text-center text-xs" style={{ color: '#64748B' }}>
          {t('legal.registerNotice')}{' '}
          <Link to="/terms" className="underline">
            {t('legal.registerLink')}
          </Link>
          .
        </p>
      </form>
    </AuthShell>
  );
}
